<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      ref="tooltipRef"
      class="guided-tour-tooltip fixed z-[60] bg-[#2D2D32] text-white rounded-lg shadow-sm p-3 w-[300px]"
      :style="tooltipStyle"
    >
      <!-- Arrow pointer -->
      <div
        class="absolute w-3 h-3 bg-[#2D2D32] transform rotate-45"
        :style="arrowStyle"
      ></div>

      <!-- Header with title and close button -->
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-base font-semibold text-white">
          <Translation :name="titleKey" :default="title" />
        </h3>
        <button
          @click="close"
          class="text-gray-400 hover:text-white transition-colors ml-2 -mt-1"
          aria-label="Close tour"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Description -->
      <p class="text-xs text-gray-300 mb-4">
        <Translation :name="descriptionKey" :default="description" />
      </p>

      <!-- Footer with step indicator and navigation buttons -->
      <div
        class="flex items-center justify-between border border-t border-transparent border-t-[#5A5A67]"
      >
        <span class="text-sm text-white font-semibold mt-2"
          >{{ currentStep }}/{{ totalSteps }}</span
        >
        <div class="flex gap-4 mt-2">
          <button
            @click="prev"
            :disabled="currentStep === 1"
            :class="[
              'px-4 py-2.5 text-sm font-semibold rounded-md transition-colors',
              currentStep === 1
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-500',
            ]"
          >
            <Translation name="guided_tour.prev_button" default="Prev" />
          </button>
          <button
            @click="next"
            class="px-4 py-2.5 text-sm font-semibold bg-brand-600 text-white rounded-md hover:bg-brand-500 transition-colors"
          >
            <Translation
              :name="
                currentStep === totalSteps
                  ? 'guided_tour.finish_button'
                  : 'guided_tour.next_button'
              "
              :default="currentStep === totalSteps ? 'Finish' : 'Next'"
            />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import Translation from "./aft/Translation.vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  titleKey: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    required: true,
  },
  descriptionKey: {
    type: String,
    default: "",
  },
  currentStep: {
    type: Number,
    required: true,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  targetElement: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close", "prev", "next"]);

const tooltipRef = ref(null);
const tooltipStyle = ref({});
const arrowStyle = ref({});

const close = () => emit("close");
const prev = () => emit("prev");
const next = () => emit("next");

// Position the tooltip next to the target element
const updatePosition = async () => {
  await nextTick();
  if (!props.targetElement || !tooltipRef.value) return;

  const targetRect = props.targetElement.getBoundingClientRect();
  const tooltipWidth = 300; // Match w-[300px]
  const tooltipHeight = tooltipRef.value.offsetHeight || 180; // Approximate height
  const isMobile = window.innerWidth < 640;
  const viewportHeight = window.innerHeight;

  if (isMobile) {
    // On mobile position tooltip below the target element
    // Align with the left edge of the target, with some padding
    const left = Math.max(
      16,
      Math.min(targetRect.left, window.innerWidth - tooltipWidth - 16)
    );

    // Check if tooltip would go off-screen at the bottom
    const wouldOverflowBottom =
      targetRect.bottom + 12 + tooltipHeight > viewportHeight;

    if (wouldOverflowBottom) {
      // Position above the target
      const top = targetRect.top - tooltipHeight - 12;

      tooltipStyle.value = {
        left: `${left}px`,
        top: `${top}px`,
      };

      // Arrow pointing down, centered on the tooltip
      arrowStyle.value = {
        left: "50%",
        bottom: "-6px",
        top: "auto",
        transform: "translateX(-50%) rotate(45deg)",
      };
    } else {
      // Position below the target
      const top = targetRect.bottom + 12;

      tooltipStyle.value = {
        left: `${left}px`,
        top: `${top}px`,
      };

      // Arrow pointing up, centered on the tooltip
      arrowStyle.value = {
        left: "50%",
        top: "-6px",
        bottom: "auto",
        transform: "translateX(-50%) rotate(45deg)",
      };
    }
  } else {
    // On tablet (≥ 768px) and desktop: position tooltip to the right of the target
    const left = targetRect.right + 12;

    // Check if tooltip would go off-screen at the bottom
    const wouldOverflowBottom = targetRect.top + tooltipHeight > viewportHeight;

    let top;
    let arrowTop;

    if (wouldOverflowBottom) {
      // Adjust top position so tooltip stays within viewport
      top = Math.max(16, viewportHeight - tooltipHeight - 16);
      // Adjust arrow position to point to the target element
      arrowTop = Math.max(16, targetRect.top + targetRect.height / 2 - top);
    } else {
      top = targetRect.top;
      arrowTop = 16;
    }

    tooltipStyle.value = {
      left: `${left}px`,
      top: `${top}px`,
    };

    // Arrow pointing left
    arrowStyle.value = {
      left: "-6px",
      top: `${arrowTop}px`,
      transform: "rotate(45deg)",
    };
  }
};

// Handle window resize
const handleResize = () => {
  if (props.isVisible && props.targetElement) {
    updatePosition();
  }
};

watch(
  () => [props.isVisible, props.targetElement],
  () => {
    if (props.isVisible && props.targetElement) {
      updatePosition();
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
